import { useLazyQuery } from '@apollo/client';
import { Button, Col, Image, Row, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteIcon, EditIcon } from '../../assets/svg';
import { MODULES, ROUTES, defaultDateFormat } from '../../common/constants';
import TableComponent from '../../components/CommonTable';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';
import Portal from '../../components/Portal';
import SearchComponent from '../../components/SearchComponent';
import AddEditModal from './AddEditModal';
import './categories.less';
import { DELETE_CATEGORY } from './graphql/Mutations';
import { CATEGORIES } from './graphql/Queries';

const Categories = () => {
  const initialUserFilter = {
    filters: { skip: 0, limit: 10 },
    sort: { sortOn: 'createdAt', sortBy: 'DESC' },
  };

  const initialPaginationValue = {
    total: 0,
    current: 1,
  };

  const navigate = useNavigate();
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({});
  const [userFilter, setUserFilter] = useState(initialUserFilter);

  const [listCompaniesDetails, { loading, data }] = useLazyQuery(CATEGORIES, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const pagination = {
        ...paginationProp,
        defaultPageSize: 10,
        total: res?.categories?.count,
      };
      setPaginationProp(pagination);
    },
    onError() {},
  });

  useEffect(() => {
    listCompaniesDetails({ variables: userFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchUserDetails = () => {
    setSortedInfo({});
    setUserFilter(initialUserFilter);
    setPaginationProp(initialPaginationValue);
    listCompaniesDetails({ variables: { ...initialUserFilter } });
  };

  const handleTableChange = (pagination, filter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setSortedInfo(sorter);

    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.column) {
      setUserFilter({
        ...userFilter,
        sort: {
          sortOn: sorter.field,
          sortBy: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      });
      listCompaniesDetails({
        variables: {
          ...userFilter,
          filters: {
            skip,
            limit: pagination.pageSize,
          },
          sort: {
            sortOn: sorter.field,
            sortBy: sorter.order === 'ascend' ? 'ASC' : 'DESC',
          },
        },
      });
    } else {
      setUserFilter({
        ...userFilter,
        filters: { skip, limit: pagination.pageSize },
        sort: { sortOn: 'createdAt', sortBy: 'DESC' },
      });
      listCompaniesDetails({
        variables: {
          ...userFilter,
          filters: {
            skip,
            limit: pagination.pageSize,
          },
          sort: { sortOn: 'createdAt', sortBy: 'DESC' },
        },
      });
    }
  };

  const columns = [
    {
      title: '#',
      key: '#',
      width: '10%',
      align: 'center',
      render: (record, _, index) => (
        <div>{userFilter?.filters?.skip + index + 1}</div>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
    },
    {
      title: 'Image',
      dataIndex: 'image',
      render: (image) => (
        <div
          onClick={(e) => {
            e?.stopPropagation();
          }}
        >
          <Image
            className="category-img"
            src={image?.url}
            alt="img"
            key={image?.key}
            height={32}
            width={32}
          />
        </div>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
      render: (date) => dayjs(date).format(defaultDateFormat),
    },
    {
      align: 'center',
      render: (id, record) => (
        <>
          <Tooltip title="Edit Category">
            <EditIcon
              className="mr-10 pointer"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRecord(record);
                setIsModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete Category">
            <DeleteIcon
              className="pointer"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRecord(record);
                setShowDeleteModal(true);
              }}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  const onSearchChange = async (value) => {
    setUserFilter({
      ...userFilter,
      filters: {
        ...userFilter?.filters,
        skip: 0,
        search: value,
      },
    });
    listCompaniesDetails({
      variables: {
        ...userFilter,
        filters: {
          ...userFilter?.filters,
          skip: 0,
          search: value,
        },
      },
    });
  };

  return (
    <div className="categories-module">
      <Portal portalId="header-wrapper">
        <h1 className="m-0 font-600">{MODULES.CATEGORIES}</h1>
      </Portal>
      <Row className="mb-24" justify="space-between">
        <Col>
          <SearchComponent
            id="search-container-id"
            name="categories"
            getData={onSearchChange}
            className="category-search search-component"
          />
        </Col>
        <Col>
          <Button
            type="primary"
            className="add-new-btn"
            onClick={() => setIsModalOpen(true)}
          >
            ADD NEW
          </Button>
        </Col>
      </Row>
      <TableComponent
        loadingData={loading}
        columns={columns}
        data={data?.categories?.categories}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(obj) => obj.id}
        onRow={(record) => ({
          onClick: () => {
            navigate(`${ROUTES.CATEGORIES}/${record?.id}/variations`);
          },
        })}
        rowClassName="pointer"
        scroll={{ y: 472 }}
      />
      {isModalOpen && (
        <AddEditModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          refetch={refetchUserDetails}
          selectedRecord={selectedRecord}
          setSelectedRecord={setSelectedRecord}
        />
      )}
      {showDeleteModal && (
        <DeleteConfirmModal
          isModalOpen={showDeleteModal}
          setIsModalOpen={setShowDeleteModal}
          refetch={refetchUserDetails}
          selectedRecord={selectedRecord}
          setSelectedRecord={setSelectedRecord}
          mutation={DELETE_CATEGORY}
          module="category"
        />
      )}
    </div>
  );
};
export default Categories;
